import type { ReactNode } from 'react'
import { useLocation, useNavigationType } from 'react-router'
import {
	anchorIdFromHref,
	attachAnchorGlide,
	glideTargetFromState,
	landOnMount,
} from '#app/utils/site/glide-scroll'
import { type Audience, SITE_LINKS } from '#app/utils/site/site-links'
import { useOptionalUser } from '#app/utils/user'
import { SiteFooter } from './SiteFooter'
import { HEADER_SELECTOR, SiteHeader } from './SiteHeader'

export interface SiteViewer {
	userType: 'tenant' | 'landlord'
	firstName?: string | null
}

/** The signed-in user as the website header sees them (also the app's
 * NavBar, which shares the mobile menu). */
export function toSiteViewer(
	user: ReturnType<typeof useOptionalUser>,
): SiteViewer | null {
	return user
		? {
				userType: user.userType === 'landlord' ? 'landlord' : 'tenant',
				firstName: user.profile?.firstName ?? null,
			}
		: null
}

const GLIDE_OPTIONS = {
	headerSelector: HEADER_SELECTOR,
	trackId: anchorIdFromHref(SITE_LINKS.howItWorks),
}

/**
 * Ref callback for the page root (stable, so it runs once per mount). It
 * makes every in-page link inside the site glide instead of jump, and lands
 * on a section another page asked for through navigation state (the root
 * carries it as `data-glide-to`). The returned cleanup detaches both.
 */
function siteRootRef(node: HTMLDivElement | null) {
	if (!node) return
	const detach = attachAnchorGlide(node, GLIDE_OPTIONS)
	const cancel = landOnMount(node, {
		...GLIDE_OPTIONS,
		glideTo: node.dataset.glideTo ?? null,
	})
	return () => {
		detach()
		cancel()
	}
}

/**
 * Wraps every public website page: scoped root class, header, footer. The
 * root layout renders no NavBar for routes whose `handle.chrome` is "site",
 * so this is the only chrome those pages get.
 */
export function SiteShell({
	audience,
	children,
	/** Set on the home pages so the header can fade over the photo hero. */
	hasPhotoHero = false,
}: {
	audience: Audience
	children: ReactNode
	hasPhotoHero?: boolean
}) {
	const user = useOptionalUser()
	const location = useLocation()
	const navigationType = useNavigationType()
	// Only a fresh link click (PUSH) may ask for a glide; back and forward
	// (POP) keep React Router's restored scroll position.
	const glideTo =
		navigationType === 'PUSH' ? glideTargetFromState(location.state) : null
	const viewer = toSiteViewer(user)
	return (
		<div
			ref={siteRootRef}
			data-glide-to={glideTo ?? undefined}
			className="tt-site flex min-h-dvh flex-col"
		>
			<SiteHeader
				audience={audience}
				viewer={viewer}
				hasPhotoHero={hasPhotoHero}
			/>
			<main className="flex-1">{children}</main>
			<SiteFooter />
		</div>
	)
}
